import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { GoalSectorModal } from '../components/GoalSectorModal';
import { goalSectorsApi } from '../budgetGoalsApi';
import type { GoalSectorListItem } from '../budgetGoalTypes';

/**
 * The goal catalog's top rung, reached through **Procurement → Investment Planning →
 * Settings**.
 *
 * All three rungs share the `budget-goals.*` key set — maintaining the hierarchy is one
 * administrative job. They do NOT share a page: the rungs have different columns, so one
 * descriptor-driven shell over three shapes would be a switch pretending to be configuration.
 */
export function GoalSectorsIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<GoalSectorListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    sector: GoalSectorListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchSectors = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      goalSectorsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (sector: GoalSectorListItem): MenuItem[] => [
      {
        key: `edit-${sector.id}`,
        label: 'Edit',
        permission: 'budget-goals.update',
        onClick: () => setEditing(sector),
      },
      {
        key: `restore-${sector.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'budget-goals.delete',
        disabledReason: sector.isActive ? 'This sector is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', sector }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await goalSectorsApi.deactivate(confirming.sector.id);
      else await goalSectorsApi.activate(confirming.sector.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<GoalSectorListItem>
        module="budget-goals"
        /*
          The three rungs share a module key, so they share what `<IndexPage>` persists per
          module — column widths, the Table/List preference, the rail's width. They do not have
          the same columns, so that is a small leak rather than the consistent reading it is for
          the four L&D vocabularies. It is the narrower of the two defects on offer; the fix if
          it bites is a key per rung.
        */
        moduleNoun="goal sectors"
        breadcrumb={[
          'Procurement',
          'Investment Planning',
          { label: 'Settings', to: '/procurement/investment-planning/settings' },
          'Sectors',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['GoalSector']}
        rowKey={(sector) => sector.id}
        fetch={fetchSectors}
        reloadKey={reloadKey}
        searchPlaceholder="Search sectors"
        emptyMessage="No sectors yet. Add the ones this organization's investment programme is organized under."
        onCreate={() => setCreating(true)}
        createPermission="budget-goals.create"
        // The anchor opens the profile; Edit is in the row menu. The READ key — opening a
        // record is not changing it.
        onRowOpen={(sector) =>
          navigate(`/procurement/investment-planning/settings/goal-sectors/${sector.id}`)
        }
        openPermission="budget-goals.read"
        rowActions={rowActions}
        onDelete={(sector) => setConfirming({ kind: 'deactivate', sector })}
        deletePermission="budget-goals.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(sector) => (sector.isActive ? null : 'This sector is already deleted.')}
        onClearData={() => goalSectorsApi.clear()}
        clearDataPermission="budget-goals.clear-data"
        otherInfo={(sector) => sector}
      />

      {creating || editing !== null ? (
        <GoalSectorModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete sector' : 'Restore sector'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`goal-sector-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.sector.name}" stops being offered when a sub-sector is filed.`
                  : `"${confirming.sector.name}" is offered again when a sub-sector is filed.`}
              </p>

              {/*
                The count is stated because the delete does LESS than its label suggests, and a
                reader who expected the branch to disappear would see nothing change. The
                children are deliberately left alone — the shape every deleted catalog row has
                here.
              */}
              {confirming.kind === 'deactivate' && confirming.sector.subSectorCount > 0 ? (
                <p>
                  The {confirming.sector.subSectorCount} sub-sector
                  {confirming.sector.subSectorCount === 1 ? '' : 's'} under it, and the{' '}
                  {confirming.sector.goalCount} goal
                  {confirming.sector.goalCount === 1 ? '' : 's'} beneath those, are kept and stay
                  in their own lists. What changes is that nothing NEW can be filed here — a
                  goal under a deleted sector stops being offered to a budget plan.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted sector can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<GoalSectorListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 280,
    flex: true,
    sortable: true,
    render: (sector) => <TextCell value={sector.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    summary: true,
    render: (sector) => <TextCell value={sector.description} />,
  },
  {
    key: 'subSectorCount',
    header: 'Sub-sectors',
    width: 120,
    align: 'right',
    sortable: true,
    summary: true,
    // A quantity: `numeric` mutes a zero, which is the ordinary case for a sector nobody has
    // filed under yet. Muted, never an em dash — "none" and "unknown" are different facts.
    render: (sector) => <TextCell value={String(sector.subSectorCount)} mono numeric />,
  },
  {
    key: 'goalCount',
    header: 'Goals',
    width: 100,
    align: 'right',
    sortable: true,
    summary: true,
    render: (sector) => <TextCell value={String(sector.goalCount)} mono numeric />,
  },
  {
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    /*
      The STORED sort value under a heading that says so, never a 1..n "level". A position is
      only correct with the full ladder in hand, and this is a paged list — see the level rule.
      It renders `mono` and NOT `numeric`: an order is an identifier here rather than a
      quantity, so a zero should not dim.
    */
    render: (sector) => <TextCell value={String(sector.sortOrder)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (sector) =>
      sector.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
